import { useEffect, useRef } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LS_KEY = "bd_install_bonus_claimed";

// Brand-colored confetti burst — navy, green, amber, white
function fireRewardConfetti() {
  const colors = ["#16a34a", "#facc15", "#0f172a", "#ffffff", "#f97316"];
  // Center burst
  confetti({
    particleCount: 80,
    spread: 75,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.35 },
    colors,
    scalar: 1.1,
    zIndex: 9999,
  });
  // Side bursts staggered for extra fun
  setTimeout(() => {
    confetti({
      particleCount: 40,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors,
      zIndex: 9999,
    });
    confetti({
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors,
      zIndex: 9999,
    });
  }, 180);
}

/**
 * Watches for the first time the user opens the app in PWA standalone
 * (Add-to-Home-Screen) mode. On that first launch, calls the backend to
 * award +1 Dash Point. Idempotent on both client (localStorage) and
 * server (user.install_bonus_claimed flag).
 *
 * Mount once near the top of the tree, inside <AuthProvider />.
 */
export default function InstallBonus() {
  const { user, refreshMe } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (!user) return;
    if (localStorage.getItem(LS_KEY)) return;

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (!isStandalone) return;

    fired.current = true;
    (async () => {
      try {
        const { data } = await api.post("/me/install-bonus");
        localStorage.setItem(LS_KEY, "1");
        if (data && data.awarded === 1) {
          fireRewardConfetti();
          toast.success("📲 +1 Dash Point earned!", {
            description: "Thanks for installing Bag Dash Kids on your home screen.",
            duration: 6000,
          });
          try {
            await refreshMe();
          } catch (_) {
            /* no-op: toast already shown */
          }
        }
      } catch (_) {
        // Silent fail — we'll try again next standalone launch
      }
    })();
  }, [user, refreshMe]);

  return null;
}
